import * as React from "react";
import {
  AiPaymentService,
  type AIAdminAnalyticsResponse,
  type AIAdminBreakdownResponse,
  type AIAdminBreakdownRow,
  type AIAdminDomainDetailResponse,
  type AIAdminLedgerResponse,
  type AIAdminLedgerRow,
  type AIAdminRequestDetailResponse,
  type AIChatUsageAdminResponse,
  type AIChatUsageAdminRow,
  type AIDailyBurnPoint,
  type AIDomainLookupRow,
  type AIDomainSummary,
  type AIPaymentAdminResponse,
  type AIPaymentAdminRow,
  type AIModelBreakdownRow,
  type AITopDomainRow,
  type AITopExpensiveRequest,
  type AITopUserRow
} from "../../../../services/ai-payment-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaNumber from "../../../ui-kit/kuika-number/kuika-number";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTable, { type KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import "./ai-chatbot-admin-panel.scss";

type AdminTab = "Overview" | "Domains" | "Users" | "Models" | "Requests" | "Credits & Payments";
type RangeKey = "7d" | "30d" | "90d" | "180d" | "MTD" | "Lifetime";
type MetricKey = "Credits" | "Cost" | "Tokens" | "Chats";
type BreakdownDimension = "domain" | "user" | "model";
type DrawerKind = "none" | "breakdown" | "domainDetail" | "requestDetail" | "ledger";

const CREDITS_PER_CENT = 1;
// Backend caps admin list pageSize at 500 (NormalizePageSize). We fetch the
// full window in one shot so KuikaTable handles sort/filter/pagination
// client-side. Requests/usage can exceed 500 — only the most recent window is
// loaded; the global search box narrows the server query before that cap.
const FULL_PAGE_SIZE = 500;
const CLIENT_PAGE_SIZE = 25;
const MAIN_TABS: AdminTab[] = ["Overview", "Domains", "Users", "Models", "Requests", "Credits & Payments"];
const RANGE_TABS: RangeKey[] = ["7d", "30d", "90d", "180d", "MTD", "Lifetime"];
const METRIC_TABS: MetricKey[] = ["Credits", "Cost", "Tokens", "Chats"];

interface ActiveFilterState {
  domainId: string | null;
  domainName: string | null;
  userId: string | null;
  userEmail: string | null;
  model: string | null;
  selectedDayIso: string | null;
}

interface DateQuery {
  range: string;
  from?: string;
  to?: string;
  dailyBurnDays: number;
}

interface State {
  allowed: boolean | null;
  activeTab: AdminTab;
  range: RangeKey;
  metric: MetricKey;
  filters: ActiveFilterState;
  excludeInternal: boolean;
  analytics: AIAdminAnalyticsResponse | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  domainBreakdown: AIAdminBreakdownResponse;
  userBreakdown: AIAdminBreakdownResponse;
  modelBreakdown: AIAdminBreakdownResponse;
  breakdownLoading: boolean;
  breakdownError: string | null;
  paymentsData: AIPaymentAdminResponse;
  paymentsLoading: boolean;
  paymentsError: string | null;
  paymentsSearch: string;
  usageData: AIChatUsageAdminResponse;
  usageLoading: boolean;
  usageError: string | null;
  usageSearch: string;
  drawerKind: DrawerKind;
  drawerTitle: string;
  drawerDimension: BreakdownDimension;
  drawerLoading: boolean;
  drawerError: string | null;
  drawerBreakdown: AIAdminBreakdownResponse;
  drawerDomainDetail: AIAdminDomainDetailResponse | null;
  drawerDomainId: string | null;
  drawerRequestDetail: AIAdminRequestDetailResponse | null;
  drawerRequestId: string | null;
  drawerLedger: AIAdminLedgerResponse;
  drawerLedgerDomainId: string | null;
  hoveredChartIndex: number | null;
  selectedChartIndex: number | null;
  domainSearch: string;
  domainOptions: AIDomainLookupRow[];
  selectedDomain: AIDomainLookupRow | null;
  amountDollars: string;
  description: string;
  submitting: boolean;
  syncing: boolean;
}

interface StatTileModel {
  label: string;
  /** Rolls to its new figure when the range, the metric or the internal-traffic switch moves it. */
  value: number;
  format: (value: number) => string;
  hint: string;
  tone: string;
}

interface RankedDomainListProps {
  rows: AITopDomainRow[];
  metric: MetricKey;
}

interface RankedUserListProps {
  rows: AITopUserRow[];
  metric: MetricKey;
}

interface ModelListProps {
  rows: AIModelBreakdownRow[];
  metric: MetricKey;
}

interface RequestPreviewProps {
  rows: AITopExpensiveRequest[];
}

interface AxiosErrorShape {
  response?: { data?: unknown };
}

interface BackendErrorBody {
  error?: string;
  message?: string;
  title?: string;
}

type LoadKey =
  | "analytics"
  | "domainBreakdown"
  | "userBreakdown"
  | "modelBreakdown"
  | "payments"
  | "usage"
  | "drawerBreakdown"
  | "drawerDomainDetail"
  | "drawerRequestDetail"
  | "drawerLedger";

const EMPTY_FILTERS: ActiveFilterState = {
  domainId: null,
  domainName: null,
  userId: null,
  userEmail: null,
  model: null,
  selectedDayIso: null
};

function emptyPaymentResponse(pageSize = 25): AIPaymentAdminResponse {
  return { rows: [], domainSummaries: [], total: 0, page: 1, pageSize };
}

function emptyUsageResponse(pageSize = 25): AIChatUsageAdminResponse {
  return { rows: [], total: 0, page: 1, pageSize };
}

function emptyBreakdownResponse(dimension: BreakdownDimension, pageSize = 25): AIAdminBreakdownResponse {
  return { rows: [], total: 0, page: 1, pageSize, dimension };
}

function emptyLedgerResponse(pageSize = 25): AIAdminLedgerResponse {
  return { rows: [], total: 0, page: 1, pageSize };
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatCost(value: number): string {
  return `$${value.toFixed(4)}`;
}

function formatCredits(credits: number): string {
  return Math.round(credits).toLocaleString("en-US");
}

function formatPercent(value: number): string {
  return `${value.toFixed(0)}%`;
}

function formatNumber(value: number): string {
  return Math.round(value).toLocaleString("en-US");
}

// Explicit en-US Intl formatters — billing data must not depend on the
// browser's locale. Lazy-initialized to dodge the older Safari Intl
// constructor cost on first render.
const FULL_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short"
});
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric"
});

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return FULL_DATE_FORMATTER.format(date);
}

function formatShortDate(iso: string | null): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return SHORT_DATE_FORMATTER.format(date);
}

function formatDaysToZero(days: number | null): string {
  if (days === null) return "No burn";
  if (days <= 0) return "0 days";
  return `${days.toFixed(days < 10 ? 1 : 0)} days`;
}

function totalTokens(input: number, output: number): number {
  return input + output;
}

class AiChatbotAdminPanel extends React.PureComponent<IWithHistoryProp & IWithContextProp, State> {
  state: State = {
    allowed: null,
    activeTab: "Overview",
    range: "30d",
    metric: "Credits",
    filters: EMPTY_FILTERS,
    excludeInternal: false,
    analytics: null,
    analyticsLoading: false,
    analyticsError: null,
    domainBreakdown: emptyBreakdownResponse("domain"),
    userBreakdown: emptyBreakdownResponse("user"),
    modelBreakdown: emptyBreakdownResponse("model"),
    breakdownLoading: false,
    breakdownError: null,
    paymentsData: emptyPaymentResponse(),
    paymentsLoading: false,
    paymentsError: null,
    paymentsSearch: "",
    usageData: emptyUsageResponse(),
    usageLoading: false,
    usageError: null,
    usageSearch: "",
    drawerKind: "none",
    drawerTitle: "",
    drawerDimension: "domain",
    drawerLoading: false,
    drawerError: null,
    drawerBreakdown: emptyBreakdownResponse("domain", FULL_PAGE_SIZE),
    drawerDomainDetail: null,
    drawerDomainId: null,
    drawerRequestDetail: null,
    drawerRequestId: null,
    drawerLedger: emptyLedgerResponse(FULL_PAGE_SIZE),
    drawerLedgerDomainId: null,
    hoveredChartIndex: null,
    selectedChartIndex: null,
    domainSearch: "",
    domainOptions: [],
    selectedDomain: null,
    amountDollars: "10",
    description: "",
    submitting: false,
    syncing: false
  };

  private paymentsSearchDebounce: ReturnType<typeof setTimeout> | null = null;
  private usageSearchDebounce: ReturnType<typeof setTimeout> | null = null;
  private redirectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isMounted_ = false;
  private abortControllers = new Map<LoadKey, AbortController>();
  private drawerPreviousFocus: HTMLElement | null = null;

  async componentDidMount() {
    this.isMounted_ = true;
    const isAdmin = await AiPaymentService.CheckAdmin();
    if (!this.isMounted_) return;
    if (!isAdmin) {
      this.safeSetState({ allowed: false });
      this.redirectTimeout = setTimeout(() => this.props.navigator.gotoRoot(), 1500);
      return;
    }

    this.safeSetState({ allowed: true });
    await Promise.all([this.loadAnalytics(), this.loadTabData("Overview")]);
  }

  componentWillUnmount() {
    this.isMounted_ = false;
    if (this.paymentsSearchDebounce) clearTimeout(this.paymentsSearchDebounce);
    if (this.usageSearchDebounce) clearTimeout(this.usageSearchDebounce);
    if (this.redirectTimeout) clearTimeout(this.redirectTimeout);
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
    document.removeEventListener("keydown", this.handleDrawerKeydown);
  }

  // Wraps setState to no-op after unmount — every async load path can complete
  // after the user navigates away.
  private safeSetState: typeof this.setState = (...args: Parameters<typeof this.setState>) => {
    if (!this.isMounted_) return;
    this.setState(...args);
  };

  // Replace the previous in-flight controller for this load key, returning the
  // signal of the new one so the caller can pass it to Axios.
  private nextSignal(key: LoadKey): AbortSignal {
    const previous = this.abortControllers.get(key);
    if (previous) previous.abort();
    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    return controller.signal;
  }

  private isCancellation(err: unknown): boolean {
    if (typeof err !== "object" || err === null) return false;
    const candidate = err as { name?: string; code?: string; message?: string };
    return (
      candidate.name === "CanceledError" ||
      candidate.name === "AbortError" ||
      candidate.code === "ERR_CANCELED" ||
      candidate.message === "canceled"
    );
  }

  private extractBackendError(err: unknown): string {
    const body = (err as AxiosErrorShape)?.response?.data;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && typeof body === "object") {
      const obj = body as BackendErrorBody;
      if (obj.error) return obj.error;
      if (obj.message) return obj.message;
      if (obj.title) return obj.title;
    }
    return "Backend error. Please retry.";
  }

  private getDateQuery(): DateQuery {
    const selectedDayIso = this.state.filters.selectedDayIso;
    if (selectedDayIso) {
      const day = new Date(selectedDayIso);
      const from = new Date(day);
      from.setUTCHours(0, 0, 0, 0);
      const to = new Date(day);
      to.setUTCHours(23, 59, 59, 999);
      return { range: "custom", from: from.toISOString(), to: to.toISOString(), dailyBurnDays: 1 };
    }

    if (this.state.range === "7d") return { range: "7d", dailyBurnDays: 7 };
    if (this.state.range === "90d") return { range: "90d", dailyBurnDays: 90 };
    if (this.state.range === "180d") return { range: "180d", dailyBurnDays: 180 };
    if (this.state.range === "MTD") return { range: "mtd", dailyBurnDays: 31 };
    if (this.state.range === "Lifetime") return { range: "lifetime", dailyBurnDays: 30 };
    return { range: "30d", dailyBurnDays: 30 };
  }

  private getMetricSort(): string {
    if (this.state.metric === "Cost") return "cost";
    if (this.state.metric === "Tokens") return "tokens";
    if (this.state.metric === "Chats") return "chats";
    return "credits";
  }

  private getChartValue(point: AIDailyBurnPoint): number {
    if (this.state.metric === "Cost") return point.costUsd;
    if (this.state.metric === "Tokens") return point.tokensInput + point.tokensOutput;
    if (this.state.metric === "Chats") return point.chatCount;
    return point.credits;
  }

  private formatMetricValue(value: number): string {
    if (this.state.metric === "Cost") return formatCost(value);
    if (this.state.metric === "Tokens") return formatNumber(value);
    if (this.state.metric === "Chats") return formatNumber(value);
    return formatCredits(value);
  }

  private metricFromDomain(row: AITopDomainRow | AIAdminBreakdownRow): number {
    if (this.state.metric === "Cost") return row.costUsd;
    if (this.state.metric === "Tokens") return row.tokensInput + row.tokensOutput;
    if (this.state.metric === "Chats") return row.chatCount;
    return row.creditsBurned;
  }

  private metricFromUser(row: AITopUserRow | AIAdminBreakdownRow): number {
    if (this.state.metric === "Cost") return row.costUsd;
    if (this.state.metric === "Tokens") return row.tokensInput + row.tokensOutput;
    if (this.state.metric === "Chats") return row.chatCount;
    return row.creditsBurned;
  }

  private metricFromModel(row: AIModelBreakdownRow | AIAdminBreakdownRow): number {
    if (this.state.metric === "Cost") return row.costUsd;
    if (this.state.metric === "Tokens") return row.tokensInput + row.tokensOutput;
    if (this.state.metric === "Chats") return row.chatCount;
    return row.creditsBurned;
  }

  private loadAnalytics = async () => {
    const signal = this.nextSignal("analytics");
    this.safeSetState({ analyticsLoading: true, analyticsError: null });
    const dateQuery = this.getDateQuery();
    const { filters, excludeInternal } = this.state;
    try {
      const analytics = await AiPaymentService.GetAdminAnalytics(
        {
          ...dateQuery,
          domainId: filters.domainId ?? undefined,
          userId: filters.userId ?? undefined,
          model: filters.model ?? undefined,
          excludeInternal
        },
        signal
      );
      this.safeSetState({ analytics, analyticsLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ analyticsLoading: false, analyticsError: this.extractBackendError(err) });
    }
  };

  private loadBreakdown = async (dimension: BreakdownDimension) => {
    const signal = this.nextSignal(
      dimension === "domain" ? "domainBreakdown" : dimension === "user" ? "userBreakdown" : "modelBreakdown"
    );
    const dateQuery = this.getDateQuery();
    const { filters, excludeInternal } = this.state;
    this.safeSetState({ breakdownLoading: true, breakdownError: null });
    try {
      const data = await AiPaymentService.GetAdminBreakdown(
        {
          dimension,
          ...dateQuery,
          page: 1,
          pageSize: FULL_PAGE_SIZE,
          sortBy: this.getMetricSort(),
          sortDirection: "desc",
          domainId: filters.domainId ?? undefined,
          userId: filters.userId ?? undefined,
          model: filters.model ?? undefined,
          excludeInternal
        },
        signal
      );
      if (dimension === "domain") this.safeSetState({ domainBreakdown: data, breakdownLoading: false });
      if (dimension === "user") this.safeSetState({ userBreakdown: data, breakdownLoading: false });
      if (dimension === "model") this.safeSetState({ modelBreakdown: data, breakdownLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ breakdownLoading: false, breakdownError: this.extractBackendError(err) });
    }
  };

  private loadPayments = async (search?: string) => {
    const signal = this.nextSignal("payments");
    const dateQuery = this.getDateQuery();
    const { filters, excludeInternal } = this.state;
    this.safeSetState({ paymentsLoading: true, paymentsError: null });
    try {
      const data = await AiPaymentService.GetAdminAllPayments(
        {
          ...dateQuery,
          domainId: filters.domainId ?? undefined,
          search: search ?? this.state.paymentsSearch,
          page: 1,
          pageSize: FULL_PAGE_SIZE,
          sortBy: "createdAt",
          sortDirection: "desc",
          excludeInternal
        },
        signal
      );
      this.safeSetState({ paymentsData: data, paymentsLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({
        paymentsData: emptyPaymentResponse(FULL_PAGE_SIZE),
        paymentsLoading: false,
        paymentsError: this.extractBackendError(err)
      });
    }
  };

  private loadUsage = async (search?: string) => {
    const signal = this.nextSignal("usage");
    const dateQuery = this.getDateQuery();
    const { filters, excludeInternal } = this.state;
    this.safeSetState({ usageLoading: true, usageError: null });
    try {
      const data = await AiPaymentService.GetAdminAllUsage(
        {
          ...dateQuery,
          domainId: filters.domainId ?? undefined,
          userId: filters.userId ?? undefined,
          model: filters.model ?? undefined,
          search: search ?? this.state.usageSearch,
          page: 1,
          pageSize: FULL_PAGE_SIZE,
          sortBy: this.getMetricSort(),
          sortDirection: "desc",
          excludeInternal
        },
        signal
      );
      this.safeSetState({ usageData: data, usageLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({
        usageData: emptyUsageResponse(FULL_PAGE_SIZE),
        usageLoading: false,
        usageError: this.extractBackendError(err)
      });
    }
  };

  private loadTabData = async (tab: AdminTab) => {
    if (tab === "Domains") await this.loadBreakdown("domain");
    if (tab === "Users") await this.loadBreakdown("user");
    if (tab === "Models") await this.loadBreakdown("model");
    if (tab === "Requests") await this.loadUsage();
    if (tab === "Credits & Payments") await this.loadPayments();
  };

  private reloadVisibleData = async () => {
    await Promise.all([this.loadAnalytics(), this.loadTabData(this.state.activeTab)]);
  };

  private handleTabChange = (value: string) => {
    const activeTab = value as AdminTab;
    this.safeSetState({ activeTab }, () => {
      void this.loadTabData(activeTab);
    });
  };

  private handleRangeChange = (value: string) => {
    this.safeSetState(
      {
        range: value as RangeKey,
        filters: { ...this.state.filters, selectedDayIso: null },
        selectedChartIndex: null
      },
      () => {
        void this.reloadVisibleData();
      }
    );
  };

  private handleMetricChange = (value: string) => {
    this.safeSetState({ metric: value as MetricKey }, () => {
      void this.reloadVisibleData();
    });
  };

  private handleExcludeInternalChange = (checked: boolean) => {
    this.safeSetState({ excludeInternal: checked }, () => {
      void this.reloadVisibleData();
    });
  };

  private applyDomainFilter = (domainId: string, domainName: string | null) => {
    this.safeSetState(
      {
        filters: {
          ...this.state.filters,
          domainId,
          domainName,
          userId: null,
          userEmail: null
        }
      },
      () => {
        void this.reloadVisibleData();
      }
    );
  };

  private applyUserFilter = (
    userId: string,
    userEmail: string | null,
    domainId?: string | null,
    domainName?: string | null
  ) => {
    this.safeSetState(
      {
        filters: {
          ...this.state.filters,
          userId,
          userEmail,
          domainId: domainId ?? this.state.filters.domainId,
          domainName: domainName ?? this.state.filters.domainName
        },
        activeTab: "Requests"
      },
      () => {
        void this.reloadVisibleData();
      }
    );
  };

  private applyModelFilter = (model: string | null) => {
    if (!model) return;
    this.safeSetState({ filters: { ...this.state.filters, model }, activeTab: "Requests" }, () => {
      void this.reloadVisibleData();
    });
  };

  private clearFilter = (key: keyof ActiveFilterState) => {
    const nextFilters: ActiveFilterState = { ...this.state.filters, [key]: null };
    if (key === "domainId") nextFilters.domainName = null;
    if (key === "domainName") nextFilters.domainId = null;
    if (key === "userId") nextFilters.userEmail = null;
    if (key === "userEmail") nextFilters.userId = null;
    this.safeSetState(
      { filters: nextFilters, selectedChartIndex: key === "selectedDayIso" ? null : this.state.selectedChartIndex },
      () => {
        void this.reloadVisibleData();
      }
    );
  };

  private clearAllFilters = () => {
    this.safeSetState({ filters: EMPTY_FILTERS, selectedChartIndex: null }, () => {
      void this.reloadVisibleData();
    });
  };

  private handleChartPointClick = (point: AIDailyBurnPoint, index: number) => {
    this.safeSetState(
      {
        filters: { ...this.state.filters, selectedDayIso: point.date },
        selectedChartIndex: index
      },
      () => {
        void this.reloadVisibleData();
      }
    );
  };

  private handlePaymentsSearchChange = (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    const search = String(value ?? "");
    this.setState({ paymentsSearch: search });
    if (this.paymentsSearchDebounce) clearTimeout(this.paymentsSearchDebounce);
    this.paymentsSearchDebounce = setTimeout(() => {
      void this.loadPayments(search);
    }, 300);
  };

  private handleUsageSearchChange = (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    const search = String(value ?? "");
    this.setState({ usageSearch: search });
    if (this.usageSearchDebounce) clearTimeout(this.usageSearchDebounce);
    this.usageSearchDebounce = setTimeout(() => {
      void this.loadUsage(search);
    }, 300);
  };

  private handleSync = async () => {
    this.setState({ syncing: true });
    try {
      await AiPaymentService.SyncPayments();
      await Promise.all([this.loadPayments(), this.loadAnalytics()]);
    } catch (err: unknown) {
      KMainFunctions.exceptionHandler(err);
    } finally {
      this.setState({ syncing: false });
    }
  };

  private handleDomainSearchChange = async (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    const search = String(value ?? "");
    this.setState({ domainSearch: search, selectedDomain: null });
    if (search.trim().length === 0) {
      this.setState({ domainOptions: [] });
      return;
    }
    const rows = await AiPaymentService.SearchDomains(search, 10);
    this.setState({ domainOptions: rows });
  };

  private handleSelectDomain = (domain: AIDomainLookupRow) => {
    this.setState({ selectedDomain: domain, domainSearch: domain.domainName, domainOptions: [] });
  };

  private handleAmountChange = (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    this.setState({ amountDollars: String(value ?? "") });
  };

  private handleDescriptionChange = (_: React.ChangeEvent<HTMLTextAreaElement>, value: string | number) => {
    this.setState({ description: String(value ?? "") });
  };

  private handleManualSubmit = async () => {
    const { selectedDomain, amountDollars, description } = this.state;
    if (!selectedDomain) return;
    const dollars = Number.parseFloat(amountDollars);
    if (Number.isNaN(dollars) || dollars <= 0) return;
    const cents = Math.round(dollars * 100);

    this.setState({ submitting: true });
    try {
      await AiPaymentService.CreateManualPayment(selectedDomain.domainId, cents, description.trim() || undefined);
      this.setState({
        domainSearch: "",
        selectedDomain: null,
        amountDollars: "10",
        description: "",
        submitting: false
      });
      await Promise.all([this.loadPayments(), this.loadAnalytics()]);
    } catch (err: unknown) {
      KMainFunctions.exceptionHandler(err);
      this.setState({ submitting: false });
    }
  };

  private openBreakdownDrawer = (dimension: BreakdownDimension) => {
    const title = dimension === "domain" ? "All domains" : dimension === "user" ? "All users" : "All models";
    this.setState(
      {
        drawerKind: "breakdown",
        drawerTitle: title,
        drawerDimension: dimension,
        drawerBreakdown: emptyBreakdownResponse(dimension, FULL_PAGE_SIZE),
        drawerError: null
      },
      () => {
        void this.loadDrawerBreakdown(dimension);
      }
    );
  };

  private loadDrawerBreakdown = async (dimension: BreakdownDimension) => {
    const signal = this.nextSignal("drawerBreakdown");
    const dateQuery = this.getDateQuery();
    const { filters, excludeInternal } = this.state;
    this.safeSetState({ drawerLoading: true, drawerError: null });
    try {
      const data = await AiPaymentService.GetAdminBreakdown(
        {
          dimension,
          ...dateQuery,
          page: 1,
          pageSize: FULL_PAGE_SIZE,
          sortBy: this.getMetricSort(),
          sortDirection: "desc",
          domainId: filters.domainId ?? undefined,
          userId: filters.userId ?? undefined,
          model: filters.model ?? undefined,
          excludeInternal
        },
        signal
      );
      this.safeSetState({ drawerBreakdown: data, drawerLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ drawerLoading: false, drawerError: this.extractBackendError(err) });
    }
  };

  private openDomainDetailDrawer = (domainId: string, domainName: string | null) => {
    this.setState(
      {
        drawerKind: "domainDetail",
        drawerTitle: domainName || "Domain detail",
        drawerDomainDetail: null,
        drawerDomainId: domainId,
        drawerError: null
      },
      () => {
        void this.loadDomainDetail(domainId);
      }
    );
  };

  private loadDomainDetail = async (domainId: string) => {
    const signal = this.nextSignal("drawerDomainDetail");
    const dateQuery = this.getDateQuery();
    this.safeSetState({ drawerLoading: true, drawerError: null });
    try {
      const detail = await AiPaymentService.GetAdminDomainDetail(
        domainId,
        {
          range: dateQuery.range,
          from: dateQuery.from,
          to: dateQuery.to,
          excludeInternal: this.state.excludeInternal
        },
        signal
      );
      this.safeSetState({ drawerDomainDetail: detail, drawerLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ drawerLoading: false, drawerError: this.extractBackendError(err) });
    }
  };

  private openRequestDetailDrawer = (requestId: string, title = "Request detail") => {
    this.setState(
      {
        drawerKind: "requestDetail",
        drawerTitle: title,
        drawerRequestDetail: null,
        drawerRequestId: requestId,
        drawerError: null
      },
      () => {
        void this.loadRequestDetail(requestId);
      }
    );
  };

  private loadRequestDetail = async (requestId: string) => {
    const signal = this.nextSignal("drawerRequestDetail");
    this.safeSetState({ drawerLoading: true, drawerError: null });
    try {
      const detail = await AiPaymentService.GetAdminRequestDetail(requestId, signal);
      this.safeSetState({ drawerRequestDetail: detail, drawerLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ drawerLoading: false, drawerError: this.extractBackendError(err) });
    }
  };

  private openLedgerDrawer = (domainId: string, domainName: string | null) => {
    this.setState(
      {
        drawerKind: "ledger",
        drawerTitle: `${domainName || "Domain"} ledger`,
        drawerLedgerDomainId: domainId,
        drawerLedger: emptyLedgerResponse(FULL_PAGE_SIZE),
        drawerError: null
      },
      () => {
        void this.loadLedger(domainId);
      }
    );
  };

  private loadLedger = async (domainId: string) => {
    const signal = this.nextSignal("drawerLedger");
    this.safeSetState({ drawerLoading: true, drawerError: null });
    try {
      const ledger = await AiPaymentService.GetAdminLedger(
        {
          domainId,
          page: 1,
          pageSize: FULL_PAGE_SIZE,
          excludeInternal: this.state.excludeInternal
        },
        signal
      );
      this.safeSetState({ drawerLedger: ledger, drawerLoading: false });
    } catch (err: unknown) {
      if (this.isCancellation(err)) return;
      this.safeSetState({ drawerLoading: false, drawerError: this.extractBackendError(err) });
    }
  };

  private closeDrawer = () => {
    this.safeSetState({
      drawerKind: "none",
      drawerTitle: "",
      drawerError: null,
      drawerDomainDetail: null,
      drawerDomainId: null,
      drawerRequestDetail: null,
      drawerRequestId: null,
      drawerLedgerDomainId: null
    });
    this.abortControllers.get("drawerBreakdown")?.abort();
    this.abortControllers.get("drawerDomainDetail")?.abort();
    this.abortControllers.get("drawerRequestDetail")?.abort();
    this.abortControllers.get("drawerLedger")?.abort();
  };

  private handleDrawerKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.state.drawerKind !== "none") {
      event.stopPropagation();
      this.closeDrawer();
    }
  };

  componentDidUpdate(_prevProps: IWithHistoryProp & IWithContextProp, prevState: State) {
    const wasOpen = prevState.drawerKind !== "none";
    const isOpen = this.state.drawerKind !== "none";
    if (!wasOpen && isOpen) {
      this.drawerPreviousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      document.addEventListener("keydown", this.handleDrawerKeydown);
    } else if (wasOpen && !isOpen) {
      document.removeEventListener("keydown", this.handleDrawerKeydown);
      const restore = this.drawerPreviousFocus;
      this.drawerPreviousFocus = null;
      if (restore && document.contains(restore)) {
        // Defer to a microtask so React finishes unmounting the drawer DOM
        // before we attempt to refocus the trigger.
        Promise.resolve().then(() => {
          if (this.isMounted_ && document.contains(restore)) {
            restore.focus();
          }
        });
      }
    }
  }

  private retryDrawer = () => {
    if (this.state.drawerKind === "breakdown") void this.loadDrawerBreakdown(this.state.drawerDimension);
    if (this.state.drawerKind === "domainDetail" && this.state.drawerDomainId) {
      void this.loadDomainDetail(this.state.drawerDomainId);
    }
    if (this.state.drawerKind === "requestDetail" && this.state.drawerRequestId) {
      void this.loadRequestDetail(this.state.drawerRequestId);
    }
    if (this.state.drawerKind === "ledger" && this.state.drawerLedgerDomainId) {
      void this.loadLedger(this.state.drawerLedgerDomainId);
    }
  };

  private renderError(message: string, retry: () => void) {
    return (
      <div className="aichat-admin__error">
        <KuikaSvg name="info-circle" width={18} height={18} />
        <div>
          <strong>{message}</strong>
          <KuikaButton type="text" size="sm" onClick={retry}>
            Retry
          </KuikaButton>
        </div>
      </div>
    );
  }

  private renderFilterBar() {
    const { filters } = this.state;
    const selectedDate = filters.selectedDayIso ? formatShortDate(filters.selectedDayIso) : null;
    const hasFilter = !!(filters.domainId || filters.userId || filters.model || filters.selectedDayIso);

    return (
      <div className="aichat-admin__filterbar">
        <div className="aichat-admin__filter-group">
          <span className="aichat-admin__filter-label">Range</span>
          <KuikaTabs
            options={RANGE_TABS}
            value={this.state.range}
            onChange={this.handleRangeChange}
            size="sm"
            disabled={!!filters.selectedDayIso}
          />
        </div>
        <div className="aichat-admin__filter-group">
          <span className="aichat-admin__filter-label">Metric</span>
          <KuikaTabs options={METRIC_TABS} value={this.state.metric} onChange={this.handleMetricChange} size="sm" />
        </div>
        <div className="aichat-admin__filter-spacer" />
        <KuikaSwitch
          label="Exclude @kuika.com"
          checked={this.state.excludeInternal}
          onChange={this.handleExcludeInternalChange}
        />
        <div className="aichat-admin__filter-chips">
          {filters.domainId && (
            <button type="button" onClick={() => this.clearFilter("domainId")}>
              Domain: {filters.domainName || filters.domainId}
              <KuikaSvg name="close" width={12} height={12} />
            </button>
          )}
          {filters.userId && (
            <button type="button" onClick={() => this.clearFilter("userId")}>
              User: {filters.userEmail || filters.userId}
              <KuikaSvg name="close" width={12} height={12} />
            </button>
          )}
          {filters.model && (
            <button type="button" onClick={() => this.clearFilter("model")}>
              Model: {filters.model}
              <KuikaSvg name="close" width={12} height={12} />
            </button>
          )}
          {selectedDate && (
            <button type="button" onClick={() => this.clearFilter("selectedDayIso")}>
              Day: {selectedDate}
              <KuikaSvg name="close" width={12} height={12} />
            </button>
          )}
          {hasFilter && (
            <KuikaButton type="text" size="sm" onClick={this.clearAllFilters}>
              Clear all
            </KuikaButton>
          )}
        </div>
      </div>
    );
  }

  private renderMetricChart(points: AIDailyBurnPoint[]) {
    const width = 720;
    const height = 220;
    const padX = 28;
    const padY = 28;

    if (points.length === 0) {
      return <div className="aichat-admin__empty-chart">No usage in this window.</div>;
    }

    const values = points.map((p) => this.getChartValue(p));
    const max = Math.max(1, ...values);
    const step = (width - padX * 2) / Math.max(1, points.length - 1);
    const coords = points.map((point, index) => {
      const value = this.getChartValue(point);
      const x = padX + index * step;
      const y = padY + (height - padY * 2) * (1 - value / max);
      return { point, index, value, x, y };
    });
    const linePath = coords
      .map((c, index) => `${index === 0 ? "M" : "L"}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
      .join(" ");
    const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(2)},${height - padY} L${coords[0].x.toFixed(2)},${height - padY} Z`;
    const hovered = this.state.hoveredChartIndex === null ? null : coords[this.state.hoveredChartIndex];
    const total = values.reduce((sum, value) => sum + value, 0);
    const peak = Math.max(...values);

    return (
      <div className="aichat-admin__chart">
        <div className="aichat-admin__chart-topline">
          <span>{this.state.metric} trend</span>
          <span>{this.formatMetricValue(total)} total</span>
          <span>{this.formatMetricValue(peak)} peak</span>
        </div>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="aichat-admin__chart-svg"
          role="img"
          aria-label={`${this.state.metric} trend`}
        >
          <defs>
            <linearGradient id="aichat-admin-chart-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line
            x1={padX}
            x2={width - padX}
            y1={height - padY}
            y2={height - padY}
            className="aichat-admin__chart-axis"
          />
          <path d={areaPath} fill="url(#aichat-admin-chart-fill)" />
          <path d={linePath} className="aichat-admin__chart-line" />
          {coords.map((c) => (
            <g key={c.point.date}>
              <rect
                x={c.x - step / 2}
                y={0}
                width={Math.max(6, step)}
                height={height}
                className="aichat-admin__chart-hit"
                onMouseEnter={() => this.setState({ hoveredChartIndex: c.index })}
                onMouseLeave={() => this.setState({ hoveredChartIndex: null })}
                onClick={() => this.handleChartPointClick(c.point, c.index)}
              />
              <circle
                cx={c.x}
                cy={c.y}
                r={this.state.selectedChartIndex === c.index ? 5 : 3}
                className={`aichat-admin__chart-dot${this.state.selectedChartIndex === c.index ? " is-selected" : ""}`}
              >
                <title>{`${formatShortDate(c.point.date)}: ${this.formatMetricValue(c.value)}`}</title>
              </circle>
            </g>
          ))}
          {hovered && (
            <g className="aichat-admin__chart-tooltip">
              <line x1={hovered.x} x2={hovered.x} y1={padY} y2={height - padY} />
              <rect x={Math.min(width - 190, Math.max(14, hovered.x - 90))} y={14} width={176} height={52} rx={8} />
              <text x={Math.min(width - 176, Math.max(28, hovered.x - 78))} y={36}>
                {formatShortDate(hovered.point.date)}
              </text>
              <text x={Math.min(width - 176, Math.max(28, hovered.x - 78))} y={55} className="value">
                {this.formatMetricValue(hovered.value)}
              </text>
            </g>
          )}
        </svg>
        <div className="aichat-admin__chart-axis-labels">
          <span>{formatShortDate(points[0].date)}</span>
          <span>{formatShortDate(points[points.length - 1].date)}</span>
        </div>
      </div>
    );
  }

  private buildStatTiles(): StatTileModel[] {
    const summary = this.state.analytics?.summary;
    const kpis = this.state.analytics?.kpis;
    const recon = this.state.analytics?.reconciliation;
    return [
      {
        label: "Credits burned",
        value: summary?.creditsBurned ?? 0,
        format: formatCredits,
        hint: `${formatNumber(summary?.chatCount ?? 0)} chats`,
        tone: "green"
      },
      {
        label: "Cost",
        value: summary?.costUsd ?? 0,
        format: formatCost,
        hint: `${formatCost(summary?.avgCostPerRequest ?? 0)} avg / request`,
        tone: "blue"
      },
      {
        label: "Tokens",
        value: (summary?.tokensInput ?? 0) + (summary?.tokensOutput ?? 0),
        format: formatNumber,
        hint: `${formatNumber(summary?.avgTokensPerRequest ?? 0)} avg / request`,
        tone: "orange"
      },
      {
        label: "Active domains",
        value: summary?.activeDomains ?? 0,
        format: formatNumber,
        hint: `${formatNumber(summary?.activeUsers ?? 0)} active users`,
        tone: "slate"
      },
      {
        label: "MTD revenue",
        value: kpis?.monthRevenueCents ?? 0,
        format: formatCents,
        hint: `${formatCents(kpis?.lifetimeRevenueCents ?? 0)} lifetime`,
        tone: "purple"
      },
      {
        label: "Reconciled",
        value: recon?.ratePercent ?? 0,
        format: formatPercent,
        hint: `${formatNumber(recon?.reconciled ?? 0)} / ${formatNumber(recon?.totalRequests ?? 0)} requests`,
        tone: "red"
      }
    ];
  }

  private renderStatTiles() {
    return (
      <div className="aichat-admin__stat-grid">
        {this.buildStatTiles().map((tile) => (
          <div key={tile.label} className={`aichat-admin__stat-tile aichat-admin__stat-tile--${tile.tone}`}>
            <span className="label">{tile.label}</span>
            <strong>
              <KuikaNumber value={tile.value} format={tile.format} />
            </strong>
            <span className="hint">{tile.hint}</span>
          </div>
        ))}
      </div>
    );
  }

  private renderDomainRankedList({ rows }: RankedDomainListProps) {
    if (rows.length === 0) return <div className="aichat-admin__empty-list">No domains in this window.</div>;
    const max = Math.max(1, ...rows.map((r) => this.metricFromDomain(r)));
    return (
      <ol className="aichat-admin__rank-list">
        {rows.map((row, index) => {
          const value = this.metricFromDomain(row);
          return (
            <li key={row.domainId}>
              <button
                type="button"
                className="aichat-admin__rank-main"
                onClick={() => this.applyDomainFilter(row.domainId, row.domainName)}
              >
                <span className="rank">{index + 1}</span>
                <span className="name">{row.domainName || "-"}</span>
                <span className="bar">
                  <span style={{ width: `${Math.max(3, (value / max) * 100)}%` }} />
                </span>
                <span className="value">{this.formatMetricValue(value)}</span>
              </button>
              <KuikaButton
                type="text"
                size="sm"
                onClick={() => this.openDomainDetailDrawer(row.domainId, row.domainName)}
              >
                Details
              </KuikaButton>
            </li>
          );
        })}
      </ol>
    );
  }

  private renderUserRankedList({ rows }: RankedUserListProps) {
    if (rows.length === 0) return <div className="aichat-admin__empty-list">No users in this window.</div>;
    const max = Math.max(1, ...rows.map((r) => this.metricFromUser(r)));
    return (
      <ol className="aichat-admin__rank-list">
        {rows.map((row, index) => {
          const value = this.metricFromUser(row);
          return (
            <li key={`${row.userId}-${row.domainId ?? ""}`}>
              <button
                type="button"
                className="aichat-admin__rank-main"
                onClick={() => this.applyUserFilter(row.userId, row.userEmail, row.domainId, row.domainName)}
              >
                <span className="rank">{index + 1}</span>
                <span className="name">
                  {row.userEmail || "-"}
                  {row.domainName ? <small>{row.domainName}</small> : null}
                </span>
                <span className="bar">
                  <span style={{ width: `${Math.max(3, (value / max) * 100)}%` }} />
                </span>
                <span className="value">{this.formatMetricValue(value)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    );
  }

  private renderModelRankedList({ rows }: ModelListProps) {
    if (rows.length === 0) return <div className="aichat-admin__empty-list">No model usage in this window.</div>;
    const max = Math.max(1, ...rows.map((r) => this.metricFromModel(r)));
    return (
      <ol className="aichat-admin__rank-list">
        {rows.map((row, index) => {
          const value = this.metricFromModel(row);
          return (
            <li key={row.model}>
              <button
                type="button"
                className="aichat-admin__rank-main"
                onClick={() => this.applyModelFilter(row.model)}
              >
                <span className="rank">{index + 1}</span>
                <span className="name">{row.model}</span>
                <span className="bar">
                  <span style={{ width: `${Math.max(3, (value / max) * 100)}%` }} />
                </span>
                <span className="value">{this.formatMetricValue(value)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    );
  }

  private renderRequestPreview({ rows }: RequestPreviewProps) {
    if (rows.length === 0) return <div className="aichat-admin__empty-list">No expensive requests in this window.</div>;
    return (
      <ul className="aichat-admin__request-preview">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => this.openRequestDetailDrawer(row.id, row.requestId || "Request detail")}
            >
              <strong>{formatCredits(row.creditsConsumed)}</strong>
              <span>{row.model || "-"}</span>
              <small>
                {row.domainName || "-"} · {row.userEmail || "-"} · {formatShortDate(row.createdAt)}
              </small>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  private renderOverview() {
    const analytics = this.state.analytics;
    if (this.state.analyticsError) return this.renderError(this.state.analyticsError, this.loadAnalytics);

    return (
      <div className={`aichat-admin__overview${this.state.analyticsLoading ? " is-loading" : ""}`}>
        {this.renderStatTiles()}
        <section className="aichat-admin__panel aichat-admin__panel--wide">
          <div className="aichat-admin__panel-header">
            <div>
              <h3>{this.state.metric} trend</h3>
              <span>{this.state.filters.selectedDayIso ? "Selected day" : `${this.state.range} window`}</span>
            </div>
          </div>
          {this.renderMetricChart(analytics?.dailyBurn ?? [])}
        </section>

        <div className="aichat-admin__overview-grid">
          <section className="aichat-admin__panel">
            <div className="aichat-admin__panel-header">
              <div>
                <h3>Top domains</h3>
                <span>Preview by selected metric</span>
              </div>
              <KuikaButton type="text" size="sm" onClick={() => this.openBreakdownDrawer("domain")}>
                See all
              </KuikaButton>
            </div>
            {this.renderDomainRankedList({ rows: analytics?.topDomains ?? [], metric: this.state.metric })}
          </section>
          <section className="aichat-admin__panel">
            <div className="aichat-admin__panel-header">
              <div>
                <h3>Top users</h3>
                <span>Heavy usage by user/domain</span>
              </div>
              <KuikaButton type="text" size="sm" onClick={() => this.openBreakdownDrawer("user")}>
                See all
              </KuikaButton>
            </div>
            {this.renderUserRankedList({ rows: analytics?.topUsers ?? [], metric: this.state.metric })}
          </section>
          <section className="aichat-admin__panel">
            <div className="aichat-admin__panel-header">
              <div>
                <h3>Model mix</h3>
                <span>Cost and token distribution</span>
              </div>
              <KuikaButton type="text" size="sm" onClick={() => this.openBreakdownDrawer("model")}>
                See all
              </KuikaButton>
            </div>
            {this.renderModelRankedList({ rows: analytics?.modelBreakdown ?? [], metric: this.state.metric })}
          </section>
          <section className="aichat-admin__panel">
            <div className="aichat-admin__panel-header">
              <div>
                <h3>Costliest requests</h3>
                <span>Request-level outliers</span>
              </div>
              <KuikaButton
                type="text"
                size="sm"
                onClick={() => this.setState({ activeTab: "Requests" }, () => void this.loadUsage())}
              >
                Open
              </KuikaButton>
            </div>
            {this.renderRequestPreview({ rows: analytics?.topExpensiveRequests ?? [] })}
          </section>
        </div>
      </div>
    );
  }

  private domainColumns(): KuikaTableColumn<AIAdminBreakdownRow>[] {
    return [
      {
        id: "domain",
        header: () => "Domain",
        cell: (r) => r.domainName || "-",
        accessorFn: (r) => r.domainName ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 220
      },
      {
        id: "metric",
        header: () => this.state.metric,
        cell: (r) => this.formatMetricValue(this.metricFromDomain(r)),
        accessorFn: (r) => this.metricFromDomain(r),
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "chats",
        header: () => "Chats",
        cell: (r) => formatNumber(r.chatCount),
        accessorFn: (r) => r.chatCount,
        enableColumnFilter: false,
        align: "right",
        width: 90
      },
      {
        id: "balance",
        header: () => "Balance",
        cell: (r) => formatCredits(r.creditsRemaining),
        accessorFn: (r) => r.creditsRemaining,
        enableColumnFilter: false,
        align: "right",
        width: 120,
        cellClassName: (r) => (r.creditsRemaining < 0 ? "aichat-admin__negative" : "")
      },
      {
        id: "burn",
        header: () => "Burn / day",
        cell: (r) => formatCredits(r.burnRateCreditsPerDay),
        accessorFn: (r) => r.burnRateCreditsPerDay,
        enableColumnFilter: false,
        align: "right",
        width: 100
      },
      {
        id: "forecast",
        header: () => "Days to zero",
        cell: (r) => formatDaysToZero(r.daysToZero),
        accessorFn: (r) => r.daysToZero ?? Number.POSITIVE_INFINITY,
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "last",
        header: () => "Last activity",
        cell: (r) => formatDate(r.lastActivityAt),
        accessorFn: (r) => r.lastActivityAt ?? "",
        enableColumnFilter: false,
        width: 170
      }
    ];
  }

  private userColumns(): KuikaTableColumn<AIAdminBreakdownRow>[] {
    return [
      {
        id: "user",
        header: () => "User",
        cell: (r) => r.userEmail || "-",
        accessorFn: (r) => r.userEmail ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 240
      },
      {
        id: "domain",
        header: () => "Domain",
        cell: (r) => r.domainName || "-",
        accessorFn: (r) => r.domainName ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 180
      },
      {
        id: "metric",
        header: () => this.state.metric,
        cell: (r) => this.formatMetricValue(this.metricFromUser(r)),
        accessorFn: (r) => this.metricFromUser(r),
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "avg",
        header: () => "Avg credits",
        cell: (r) => formatCredits(r.avgCreditsPerRequest),
        accessorFn: (r) => r.avgCreditsPerRequest,
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "cost",
        header: () => "Cost",
        cell: (r) => formatCost(r.costUsd),
        accessorFn: (r) => r.costUsd,
        enableColumnFilter: false,
        align: "right",
        width: 110
      },
      {
        id: "last",
        header: () => "Last activity",
        cell: (r) => formatDate(r.lastActivityAt),
        accessorFn: (r) => r.lastActivityAt ?? "",
        enableColumnFilter: false,
        width: 170
      }
    ];
  }

  private modelColumns(): KuikaTableColumn<AIAdminBreakdownRow>[] {
    return [
      {
        id: "model",
        header: () => "Model",
        cell: (r) => <span className="aichat-admin__mono">{r.model || "-"}</span>,
        accessorFn: (r) => r.model ?? "",
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 260
      },
      {
        id: "metric",
        header: () => this.state.metric,
        cell: (r) => this.formatMetricValue(this.metricFromModel(r)),
        accessorFn: (r) => this.metricFromModel(r),
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "chats",
        header: () => "Chats",
        cell: (r) => formatNumber(r.chatCount),
        accessorFn: (r) => r.chatCount,
        enableColumnFilter: false,
        align: "right",
        width: 90
      },
      {
        id: "avgCost",
        header: () => "Avg cost",
        cell: (r) => formatCost(r.avgCostPerRequest),
        accessorFn: (r) => r.avgCostPerRequest,
        enableColumnFilter: false,
        align: "right",
        width: 110
      },
      {
        id: "avgCredits",
        header: () => "Avg credits",
        cell: (r) => formatCredits(r.avgCreditsPerRequest),
        accessorFn: (r) => r.avgCreditsPerRequest,
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "tokens",
        header: () => "Tokens",
        cell: (r) => formatNumber(totalTokens(r.tokensInput, r.tokensOutput)),
        accessorFn: (r) => totalTokens(r.tokensInput, r.tokensOutput),
        enableColumnFilter: false,
        align: "right",
        width: 120
      }
    ];
  }

  private requestColumns(): KuikaTableColumn<AIChatUsageAdminRow>[] {
    return [
      {
        id: "createdAt",
        header: () => "Date",
        cell: (r) => formatDate(r.createdAt),
        accessorFn: (r) => r.createdAt ?? "",
        enableColumnFilter: false,
        width: 170
      },
      {
        id: "domain",
        header: () => "Domain",
        cell: (r) => r.domainName || "-",
        accessorFn: (r) => r.domainName ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 180
      },
      {
        id: "user",
        header: () => "User",
        cell: (r) => r.userEmail || "-",
        accessorFn: (r) => r.userEmail ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 220
      },
      {
        id: "model",
        header: () => "Model",
        cell: (r) => <span className="aichat-admin__mono">{r.model || "-"}</span>,
        accessorFn: (r) => r.model ?? "",
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 230
      },
      {
        id: "tokens",
        header: () => "Tokens",
        cell: (r) => `${formatNumber(r.tokensInput)} / ${formatNumber(r.tokensOutput)}`,
        accessorFn: (r) => totalTokens(r.tokensInput, r.tokensOutput),
        enableColumnFilter: false,
        align: "right",
        width: 140
      },
      {
        id: "cost",
        header: () => "Cost",
        cell: (r) => formatCost(r.costUsd),
        accessorFn: (r) => r.costUsd,
        enableColumnFilter: false,
        align: "right",
        width: 100
      },
      {
        id: "credits",
        header: () => "Credits",
        cell: (r) => formatCredits(r.creditsConsumed),
        accessorFn: (r) => r.creditsConsumed,
        enableColumnFilter: false,
        align: "right",
        width: 100
      },
      {
        id: "status",
        header: () => "Status",
        cell: (r) => (
          <span className={`aichat-admin__pill ${r.reconciled ? "is-final" : "is-estimated"}`}>
            {r.reconciled ? "Final" : "Estimated"}
          </span>
        ),
        accessorFn: (r) => (r.reconciled ? "Final" : "Estimated"),
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 110
      }
    ];
  }

  private paymentColumns(): KuikaTableColumn<AIPaymentAdminRow>[] {
    return [
      {
        id: "createdAt",
        header: () => "Date",
        cell: (r) => formatDate(r.createdAt),
        accessorFn: (r) => r.createdAt ?? "",
        enableColumnFilter: false,
        width: 170
      },
      {
        id: "domain",
        header: () => "Domain",
        cell: (r) => r.domainName || "-",
        accessorFn: (r) => r.domainName ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 220
      },
      {
        id: "amount",
        header: () => "Amount",
        cell: (r) => formatCents(r.amountCents),
        accessorFn: (r) => r.amountCents,
        enableColumnFilter: false,
        align: "right",
        width: 110
      },
      {
        id: "credits",
        header: () => "Credits",
        cell: (r) => formatCredits(r.credits),
        accessorFn: (r) => r.credits,
        enableColumnFilter: false,
        align: "right",
        width: 120
      },
      {
        id: "status",
        header: () => "Type",
        cell: (r) => (
          <span className={`aichat-admin__pill ${r.isManualEntry ? "is-manual" : "is-final"}`}>
            {r.isManualEntry ? "Manual" : "Stripe"}
          </span>
        ),
        accessorFn: (r) => (r.isManualEntry ? "Manual" : "Stripe"),
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 110
      },
      {
        id: "createdBy",
        header: () => "Added by",
        cell: (r) => r.createdByEmail || "-",
        accessorFn: (r) => r.createdByEmail ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 220
      },
      {
        id: "description",
        header: () => "Description",
        cell: (r) => <span className="aichat-admin__truncate">{r.description || "-"}</span>,
        accessorFn: (r) => r.description ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 240
      }
    ];
  }

  private ledgerColumns(): KuikaTableColumn<AIAdminLedgerRow>[] {
    return [
      {
        id: "createdAt",
        header: () => "Date",
        cell: (r) => formatDate(r.createdAt),
        accessorFn: (r) => r.createdAt ?? "",
        enableColumnFilter: false,
        width: 170
      },
      {
        id: "type",
        header: () => "Type",
        cell: (r) => <span className="aichat-admin__mono">{r.type}</span>,
        accessorFn: (r) => r.type ?? "",
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 130
      },
      {
        id: "delta",
        header: () => "Credits",
        cell: (r) => formatCredits(r.creditsDelta),
        accessorFn: (r) => r.creditsDelta,
        enableColumnFilter: false,
        align: "right",
        width: 120,
        cellClassName: (r) => (r.creditsDelta < 0 ? "aichat-admin__negative" : "aichat-admin__positive")
      },
      {
        id: "cost",
        header: () => "Cost",
        cell: (r) => (r.costUsd === null ? "-" : formatCost(r.costUsd)),
        accessorFn: (r) => r.costUsd ?? Number.NEGATIVE_INFINITY,
        enableColumnFilter: false,
        align: "right",
        width: 100
      },
      {
        id: "user",
        header: () => "User",
        cell: (r) => r.userEmail || "-",
        accessorFn: (r) => r.userEmail ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 220
      },
      {
        id: "ref",
        header: () => "Reference",
        cell: (r) => <span className="aichat-admin__truncate">{r.referenceId || r.description || "-"}</span>,
        accessorFn: (r) => r.referenceId || r.description || "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 240
      }
    ];
  }

  private renderBreakdownTable(dimension: BreakdownDimension) {
    const data =
      dimension === "domain"
        ? this.state.domainBreakdown
        : dimension === "user"
          ? this.state.userBreakdown
          : this.state.modelBreakdown;
    const columns =
      dimension === "domain" ? this.domainColumns() : dimension === "user" ? this.userColumns() : this.modelColumns();
    const countLabel = dimension === "domain" ? "Domains" : dimension === "user" ? "Users" : "Models";

    if (this.state.breakdownError)
      return this.renderError(this.state.breakdownError, () => void this.loadBreakdown(dimension));

    return (
      <KuikaTable<AIAdminBreakdownRow>
        data={data.rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={this.state.breakdownLoading}
        enableSorting
        enableColumnFilters
        pageSize={CLIENT_PAGE_SIZE}
        showCountLabel={countLabel}
        onRowClick={(row) => {
          if (dimension === "domain" && row.domainId) this.openDomainDetailDrawer(row.domainId, row.domainName);
          if (dimension === "user" && row.userId)
            this.applyUserFilter(row.userId, row.userEmail, row.domainId, row.domainName);
          if (dimension === "model") this.applyModelFilter(row.model);
        }}
        emptyTitle={`No ${dimension} rows`}
        emptyDescription="Try a wider date range or clear active filters."
      />
    );
  }

  private renderRequestsTab() {
    return (
      <section className="aichat-admin__panel aichat-admin__panel--wide">
        <div className="aichat-admin__table-toolbar">
          <KuikaInput
            type="text"
            size="sm"
            value={this.state.usageSearch}
            onChange={this.handleUsageSearchChange}
            placeholder="Search request, model, domain, or user"
            leftIcon={<KuikaSvg name="search" width={14} height={14} />}
          />
        </div>
        {this.state.usageError ? (
          this.renderError(this.state.usageError, () => void this.loadUsage())
        ) : (
          <KuikaTable<AIChatUsageAdminRow>
            data={this.state.usageData.rows}
            columns={this.requestColumns()}
            rowKey={(row) => row.id}
            loading={this.state.usageLoading}
            enableSorting
            enableColumnFilters
            pageSize={CLIENT_PAGE_SIZE}
            showCountLabel="Requests"
            onRowClick={(row) => this.openRequestDetailDrawer(row.id, row.requestId || "Request detail")}
            emptyTitle="No requests"
            emptyDescription="Try a wider date range or clear active filters."
          />
        )}
      </section>
    );
  }

  private renderManualForm() {
    const { selectedDomain, amountDollars, domainSearch, domainOptions, description, submitting } = this.state;
    const dollars = Number.parseFloat(amountDollars);
    const validAmount = !Number.isNaN(dollars) && dollars > 0;
    const credits = validAmount ? Math.round(dollars * 100) * CREDITS_PER_CENT : 0;
    const canSubmit = !!selectedDomain && validAmount && !submitting;

    return (
      <section className="aichat-admin__manual">
        <div className="aichat-admin__panel-header">
          <div>
            <h3>Manual grant</h3>
            <span>Add durable credits to a domain</span>
          </div>
        </div>
        <div className="aichat-admin__form-grid">
          <div className="aichat-admin__form-field">
            <span>Domain</span>
            <div className="aichat-admin__autocomplete">
              <KuikaInput
                type="text"
                size="md"
                value={domainSearch}
                onChange={this.handleDomainSearchChange}
                placeholder="Search domain"
              />
              {domainOptions.length > 0 && (
                <div className="aichat-admin__autocomplete-results">
                  {domainOptions.map((domain) => (
                    <button key={domain.domainId} type="button" onClick={() => this.handleSelectDomain(domain)}>
                      {domain.domainName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="aichat-admin__form-field">
            <span>Amount (USD)</span>
            <KuikaInput
              type="number"
              size="md"
              min={0}
              precision={2}
              value={amountDollars}
              onChange={this.handleAmountChange}
              leftUnit="$"
            />
            <small>{validAmount ? `${formatCredits(credits)} credits` : "Enter a positive amount"}</small>
          </div>
          <div className="aichat-admin__form-field aichat-admin__form-wide">
            <span>Description</span>
            <KuikaInput
              type="textarea"
              rows={3}
              value={description}
              onChange={this.handleDescriptionChange}
              placeholder="Reason"
            />
          </div>
          <div className="aichat-admin__form-actions">
            <KuikaButton
              type="primary"
              size="md"
              loading={submitting}
              disabled={!canSubmit}
              onClick={this.handleManualSubmit}
            >
              Grant credits
            </KuikaButton>
          </div>
        </div>
      </section>
    );
  }

  private domainsWithCreditsColumns(): KuikaTableColumn<AIDomainSummary>[] {
    return [
      {
        id: "domain",
        header: () => "Domain",
        cell: (r) => r.domainName || "-",
        accessorFn: (r) => r.domainName ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 240
      },
      {
        id: "creditsRemaining",
        header: () => "Credits Remaining",
        cell: (r) => formatCredits(r.creditsRemaining),
        accessorFn: (r) => r.creditsRemaining,
        enableColumnFilter: false,
        align: "right",
        width: 170,
        cellClassName: (r) => (r.creditsRemaining < 0 ? "aichat-admin__negative" : "aichat-admin__positive")
      },
      {
        id: "totalPurchased",
        header: () => "Total Purchased",
        cell: (r) => formatCents(r.totalPurchasedCents),
        accessorFn: (r) => r.totalPurchasedCents,
        enableColumnFilter: false,
        align: "right",
        width: 150
      },
      {
        id: "lifetimeSpent",
        header: () => "Lifetime Spent",
        cell: (r) => formatCost(r.lifetimeSpentUsd),
        accessorFn: (r) => r.lifetimeSpentUsd,
        enableColumnFilter: false,
        align: "right",
        width: 140
      },
      {
        id: "lastActivity",
        header: () => "Last Activity",
        cell: (r) => formatDate(r.lastActivityAt),
        accessorFn: (r) => r.lastActivityAt ?? "",
        enableColumnFilter: false,
        width: 180
      },
      {
        id: "topUser",
        header: () => "Top User",
        cell: (r) => <span className="aichat-admin__truncate">{r.topUserEmail || "-"}</span>,
        accessorFn: (r) => r.topUserEmail ?? "",
        enableColumnFilter: true,
        filterVariant: "text",
        width: 200
      }
    ];
  }

  private renderDomainsWithCredits() {
    const summaries: AIDomainSummary[] = this.state.paymentsData.domainSummaries ?? [];
    const rows: AIDomainSummary[] = summaries
      .filter((row: AIDomainSummary) => row.creditsRemaining > 0)
      .sort((a: AIDomainSummary, b: AIDomainSummary) => b.creditsRemaining - a.creditsRemaining);

    return (
      <section className="aichat-admin__panel aichat-admin__panel--wide">
        <div className="aichat-admin__panel-header">
          <div>
            <h3>Domains with credits</h3>
            <span>{`${rows.length} domain${rows.length === 1 ? "" : "s"} with active credit balance`}</span>
          </div>
        </div>
        <KuikaTable<AIDomainSummary>
          data={rows}
          columns={this.domainsWithCreditsColumns()}
          rowKey={(row) => row.domainId}
          loading={this.state.paymentsLoading}
          enableSorting
          enableColumnFilters
          pageSize={CLIENT_PAGE_SIZE}
          onRowClick={(row) => this.openDomainDetailDrawer(row.domainId, row.domainName)}
          emptyTitle="No domains with credits"
          emptyDescription="No active credit balances found in the current range."
        />
      </section>
    );
  }

  private renderCreditsPaymentsTab() {
    return (
      <div className="aichat-admin__payments-grid">
        {this.renderManualForm()}
        {this.renderDomainsWithCredits()}
        <section className="aichat-admin__panel aichat-admin__panel--wide">
          <div className="aichat-admin__table-toolbar">
            <KuikaInput
              type="text"
              size="sm"
              value={this.state.paymentsSearch}
              onChange={this.handlePaymentsSearchChange}
              placeholder="Search domain, email, or Stripe session"
              leftIcon={<KuikaSvg name="search" width={14} height={14} />}
            />
            <KuikaButton
              type="secondary"
              size="sm"
              loading={this.state.syncing}
              disabled={this.state.syncing}
              onClick={this.handleSync}
            >
              Sync Stripe
            </KuikaButton>
          </div>
          {this.state.paymentsError ? (
            this.renderError(this.state.paymentsError, () => void this.loadPayments())
          ) : (
            <KuikaTable<AIPaymentAdminRow>
              data={this.state.paymentsData.rows}
              columns={this.paymentColumns()}
              rowKey={(row) => row.id}
              loading={this.state.paymentsLoading}
              enableSorting
              enableColumnFilters
              pageSize={CLIENT_PAGE_SIZE}
              showCountLabel="Payments"
              emptyTitle="No payments"
              emptyDescription="Stripe and manual grants appear here."
            />
          )}
        </section>
      </div>
    );
  }

  /** One main tab's content, by tab key — rendered per pane, so the leaving pane keeps drawing its own tab. */
  private renderTab = (tab: string): React.ReactNode => {
    if (tab === "Overview") return this.renderOverview();
    if (tab === "Domains")
      return (
        <section className="aichat-admin__panel aichat-admin__panel--wide">
          {this.renderBreakdownTable("domain")}
        </section>
      );
    if (tab === "Users")
      return (
        <section className="aichat-admin__panel aichat-admin__panel--wide">{this.renderBreakdownTable("user")}</section>
      );
    if (tab === "Models")
      return (
        <section className="aichat-admin__panel aichat-admin__panel--wide">
          {this.renderBreakdownTable("model")}
        </section>
      );
    if (tab === "Requests") return this.renderRequestsTab();
    return this.renderCreditsPaymentsTab();
  };

  private renderDomainDetailDrawer() {
    const detail = this.state.drawerDomainDetail;
    if (!detail) return null;
    return (
      <div className="aichat-admin__drawer-body">
        <div className="aichat-admin__detail-stats">
          <div>
            <span>Balance</span>
            <strong className={detail.creditsRemaining < 0 ? "aichat-admin__negative" : ""}>
              {formatCredits(detail.creditsRemaining)}
            </strong>
          </div>
          <div>
            <span>Purchased</span>
            <strong>{formatCents(detail.totalPurchasedCents)}</strong>
          </div>
          <div>
            <span>Granted</span>
            <strong>{formatCredits(detail.totalGrantedCredits)}</strong>
          </div>
          <div>
            <span>Consumed</span>
            <strong>{formatCredits(detail.creditsConsumed)}</strong>
          </div>
          <div>
            <span>Burn / day</span>
            <strong>{formatCredits(detail.burnRateCreditsPerDay)}</strong>
          </div>
          <div>
            <span>Days to zero</span>
            <strong>{formatDaysToZero(detail.daysToZero)}</strong>
          </div>
        </div>
        {this.renderMetricChart(detail.dailyBurn)}
        <div className="aichat-admin__drawer-section">
          <div className="aichat-admin__panel-header">
            <h4>Top users</h4>
          </div>
          {this.renderUserRankedList({ rows: detail.topUsers, metric: this.state.metric })}
        </div>
        <div className="aichat-admin__drawer-section">
          <div className="aichat-admin__panel-header">
            <h4>Model mix</h4>
          </div>
          {this.renderModelRankedList({ rows: detail.modelBreakdown, metric: this.state.metric })}
        </div>
        <div className="aichat-admin__drawer-section">
          <div className="aichat-admin__panel-header">
            <h4>Latest requests</h4>
          </div>
          <ul className="aichat-admin__compact-list">
            {detail.latestRequests.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => this.openRequestDetailDrawer(row.id, row.requestId || "Request detail")}
                >
                  <span>{row.model || "-"}</span>
                  <strong>{formatCredits(row.creditsConsumed)}</strong>
                  <small>{formatDate(row.createdAt)}</small>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="aichat-admin__drawer-section">
          <div className="aichat-admin__panel-header">
            <h4>Ledger preview</h4>
            <KuikaButton
              type="text"
              size="sm"
              onClick={() => this.openLedgerDrawer(detail.domainId, detail.domainName)}
            >
              See all
            </KuikaButton>
          </div>
          <ul className="aichat-admin__compact-list">
            {detail.ledgerPreview.map((row) => (
              <li key={row.id}>
                <span>{row.type}</span>
                <strong className={row.creditsDelta < 0 ? "aichat-admin__negative" : "aichat-admin__positive"}>
                  {formatCredits(row.creditsDelta)}
                </strong>
                <small>{formatDate(row.createdAt)}</small>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  private renderRequestDetailDrawer() {
    const detail = this.state.drawerRequestDetail;
    if (!detail) return null;
    return (
      <div className="aichat-admin__drawer-body">
        <div className="aichat-admin__detail-stats">
          <div>
            <span>Credits</span>
            <strong>{formatCredits(detail.creditsConsumed)}</strong>
          </div>
          <div>
            <span>Cost</span>
            <strong>{formatCost(detail.costUsd)}</strong>
          </div>
          <div>
            <span>Total tokens</span>
            <strong>{formatNumber(detail.totalTokens)}</strong>
          </div>
          <div>
            <span>Output ratio</span>
            <strong>{detail.outputTokenRatio.toFixed(1)}%</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{detail.reconciled ? "Final" : "Estimated"}</strong>
          </div>
          <div>
            <span>Date</span>
            <strong>{formatDate(detail.createdAt)}</strong>
          </div>
        </div>
        <dl className="aichat-admin__detail-list">
          <dt>Request ID</dt>
          <dd>{detail.requestId || "-"}</dd>
          <dt>Domain</dt>
          <dd>{detail.domainName || "-"}</dd>
          <dt>User</dt>
          <dd>{detail.userEmail || "-"}</dd>
          <dt>Model</dt>
          <dd>{detail.model || "-"}</dd>
          <dt>Tokens</dt>
          <dd>
            {formatNumber(detail.tokensInput)} input / {formatNumber(detail.tokensOutput)} output
          </dd>
        </dl>
      </div>
    );
  }

  private renderDrawerBreakdown() {
    const dimension = this.state.drawerDimension;
    const columns =
      dimension === "domain" ? this.domainColumns() : dimension === "user" ? this.userColumns() : this.modelColumns();
    const countLabel = dimension === "domain" ? "Domains" : dimension === "user" ? "Users" : "Models";
    return (
      <div className="aichat-admin__drawer-table">
        <KuikaTable<AIAdminBreakdownRow>
          data={this.state.drawerBreakdown.rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={this.state.drawerLoading}
          enableSorting
          enableColumnFilters
          pageSize={CLIENT_PAGE_SIZE}
          showCountLabel={countLabel}
          onRowClick={(row) => {
            if (dimension === "domain" && row.domainId) this.openDomainDetailDrawer(row.domainId, row.domainName);
            if (dimension === "user" && row.userId)
              this.applyUserFilter(row.userId, row.userEmail, row.domainId, row.domainName);
            if (dimension === "model") this.applyModelFilter(row.model);
          }}
          emptyTitle="No rows"
          emptyDescription="Try a wider date range or clear filters."
        />
      </div>
    );
  }

  private renderLedgerDrawer() {
    return (
      <div className="aichat-admin__drawer-table">
        <KuikaTable<AIAdminLedgerRow>
          data={this.state.drawerLedger.rows}
          columns={this.ledgerColumns()}
          rowKey={(row) => row.id}
          loading={this.state.drawerLoading}
          enableSorting
          enableColumnFilters
          pageSize={CLIENT_PAGE_SIZE}
          showCountLabel="Entries"
          emptyTitle="No ledger rows"
          emptyDescription="No payments, grants, or usage rows were found."
        />
      </div>
    );
  }

  private renderDrawerContent() {
    if (this.state.drawerError) return this.renderError(this.state.drawerError, this.retryDrawer);
    if (this.state.drawerKind === "breakdown") return this.renderDrawerBreakdown();
    if (this.state.drawerKind === "domainDetail") return this.renderDomainDetailDrawer();
    if (this.state.drawerKind === "requestDetail") return this.renderRequestDetailDrawer();
    if (this.state.drawerKind === "ledger") return this.renderLedgerDrawer();
    return null;
  }

  private renderDrawer() {
    if (this.state.drawerKind === "none") return null;
    const titleId = "aichat-admin-drawer-title";
    return (
      <div className="aichat-admin__drawer-shell">
        <button
          type="button"
          className="aichat-admin__drawer-backdrop"
          onClick={this.closeDrawer}
          aria-label="Close drawer"
        />
        <aside className="aichat-admin__drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <header className="aichat-admin__drawer-header">
            <div>
              <span>
                {this.state.metric} · {this.state.filters.selectedDayIso ? "selected day" : this.state.range}
              </span>
              <h3 id={titleId}>{this.state.drawerTitle}</h3>
            </div>
            <KuikaButton type="tertiary" size="sm" isIcon onClick={this.closeDrawer}>
              <KuikaSvg name="close" width={16} height={16} />
            </KuikaButton>
          </header>
          {this.state.drawerLoading && this.state.drawerKind !== "breakdown" && this.state.drawerKind !== "ledger" ? (
            <div className="aichat-admin__drawer-loading">Loading…</div>
          ) : (
            this.renderDrawerContent()
          )}
        </aside>
      </div>
    );
  }

  render() {
    const { allowed } = this.state;

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="shield-check" />
            <KuikaH4 data-testid="aiChatbotAdminPageTitle">AI Chatbot Admin</KuikaH4>
          </div>
        </div>

        {allowed === null && <div className="aichat-admin__forbidden">Verifying admin access…</div>}

        {allowed === false && (
          <div className="aichat-admin__forbidden">
            <h2>Not authorized</h2>
            <p>You don't have access to the AI Chatbot Admin page. Redirecting…</p>
          </div>
        )}

        {allowed === true && (
          <div className="aichat-admin">
            {this.renderFilterBar()}
            <div className="aichat-admin__tabs">
              <KuikaTabs options={MAIN_TABS} value={this.state.activeTab} onChange={this.handleTabChange} size="md" />
            </div>
            <KuikaTabContent activeKey={this.state.activeTab} order={MAIN_TABS} render={this.renderTab} />
            {this.renderDrawer()}
          </div>
        )}
      </div>
    );
  }
}

const wrapped = withContext<{}>(withManagement<{}>(withHistory<{}>(AiChatbotAdminPanel)));
export default wrapped;
